//// filepath: c:\Users\HP\OneDrive\Desktop\solo\solo-dash\src\components\form-components\form-search-input.components.tsx
import { useState, useMemo } from "react";
import { FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import StarIcon from "../../assets/svgs/common/star.svg";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { SearchBox } from "../custom/search-box";

type Option = { value: string; label: string };

type FormSearchInputProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  placeholder: string;
  required?: boolean;
  options: Option[];
  showAllOnEmpty?: boolean;
  emptyText?: string;
  maxResultsWhileSearching?: number; 
  disabled?: boolean;
};

export const FormSearchInput = <T extends FieldValues>({
  form,
  name,
  label,
  placeholder,
  required = false,
  options,
  showAllOnEmpty = true,
  emptyText = "No results found",
  maxResultsWhileSearching = 5, 
  disabled = false,
}: FormSearchInputProps<T>) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return showAllOnEmpty ? options : [];
    const matched = options.filter((o) => o.label.toLowerCase().includes(q));
    return matched.slice(0, maxResultsWhileSearching); // limit while searching
  }, [options, query, showAllOnEmpty, maxResultsWhileSearching]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field: { value, onChange }, fieldState }) => {
        const selectedOption = options.find((o) => o.value === value);
        const inputDisplay =
          typing || !selectedOption ? query : selectedOption.label;

        return (
          <FormItem>
            <FormLabel className="flex items-center gap-1 font-semibold">
              {label} {required && <StarIcon />}
            </FormLabel>
            <FormControl>
              <SearchBox
                typing={typing}
                selected={!!selectedOption}
                className={`py-6 pl-6 h-16 placeholder:text-secondary ${fieldState.error ? "border-red-500" : ""}`}
                placeholder={placeholder}
                value={inputDisplay}
                defaultValue={inputDisplay}
                disabled={disabled}
                onChange={(e) => {
                  const val = e.target.value;
                  setQuery(val);
                  setTyping(true);
                  if (!open) setOpen(true);
                  if (selectedOption) {
                    // user started editing -> clear selection
                    onChange("");
                  }
                }}
                // onClear prop removed because SearchBox does not support it
              >
                <div className="flex flex-col w-full">
                  {filtered.length === 0 ? (
                    <div className="px-4 py-4 text-sm">{emptyText}</div>
                  ) : (
                    filtered.map((opt) => {
                      const active = opt.value === value;
                      return (
                        // biome-ignore lint/a11y/useKeyWithClickEvents:
                        <div
                          key={opt.value}
                          onClick={() => {
                            onChange(opt.value);
                            setQuery(opt.label);
                            setTyping(false);
                            setOpen(false);
                          }}
                          className={`px-4 pt-4 pb-4 rounded-xl cursor-pointer hover:bg-tertiarybuttonbg ${
                            active ? "bg-tertiarybuttonbg font-semibold" : ""
                          }`}
                        >
                          {opt.label}
                        </div>
                      );
                    })
                  )}
                </div>
              </SearchBox>
            </FormControl>
            {fieldState.error && (
              <p className="mt-1 text-red-500 text-xs">
                {fieldState.error.message}
              </p>
            )}
          </FormItem>
        );
      }}
    />
  );
};
